import { useState } from 'react'
import { SuggestionsList } from '@/components/suggestions/SuggestionsList'
import { Button } from '@brent/core-ui/ui'
import { Download, Loader2, Trash2 } from 'lucide-react'
import { useCleanupOrphanedSuggestions, useExportSuggestions } from '@/hooks/useSuggestions'
import { useToast } from '@/hooks/use-toast'
import { ManagementTabs } from '@/components/shared/ManagementTabs'
import { exportSuggestionsAsCsv } from '@/utils/exportCsv'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@brent/core-ui/ui'

export function Suggestions() {
  const [showCleanupDialog, setShowCleanupDialog] = useState(false)
  const cleanupMutation = useCleanupOrphanedSuggestions()
  const exportMutation = useExportSuggestions()
  const { toast } = useToast()

  const handleCleanup = async () => {
    try {
      const result = await cleanupMutation.mutateAsync()
      toast({
        title: 'Cleanup completed',
        description: `Checked ${result.checked} suggestions. Deleted ${result.deleted} orphaned suggestions.${result.failed > 0 ? ` ${result.failed} failed to delete.` : ''}`,
      })
      setShowCleanupDialog(false)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Cleanup failed',
        description: error instanceof Error ? error.message : 'An error occurred',
      })
    }
  }

  const handleExportCsv = async () => {
    try {
      const suggestions = await exportMutation.mutateAsync()
      exportSuggestionsAsCsv(suggestions)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Export failed',
        description: error instanceof Error ? error.message : 'An error occurred',
      })
    }
  }

  return (
    <div className="space-y-6">
      <ManagementTabs />

      <div className="flex items-start justify-between">
        <p className="text-muted-foreground">
          View all suggestions grouped by piece (Wikidata ID or normalized text)
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportCsv} disabled={exportMutation.isPending}>
            {exportMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {exportMutation.isPending ? 'Exporting...' : 'Download CSV'}
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowCleanupDialog(true)}
            disabled={cleanupMutation.isPending}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clean Up Orphaned
          </Button>
        </div>
      </div>

      <SuggestionsList />

      <AlertDialog open={showCleanupDialog} onOpenChange={setShowCleanupDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clean up orphaned suggestions?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all suggestions from users that no longer exist in the
              system. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleCleanup} disabled={cleanupMutation.isPending}>
              {cleanupMutation.isPending ? 'Cleaning up...' : 'Clean Up'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
